// Pure helpers + types for SOW form state. Mirrors the shape locked in
// meetingFormState.ts so the two forms share idioms — modal state lives
// in the modal component; this module owns serialisation and field-error
// types only.
//
// `numberOverrideMode` flags whether the user has clicked the
// AutoIdentifierField's Edit affordance. Empty `number` ⇒ omit from the
// create payload (backend auto-generates SOW-{YYYY}-{NNN}); non-empty
// override ⇒ send the literal value (backend uniqueness-checks via the
// TRACKING_NUMBER sentinel item and surfaces a 409 on collision).
//
// `originalValue` is held as a string for input control fidelity (empty
// state, partial typing); the serialiser parses it to an integer at
// payload time. Backend stores ex-GST AUD whole dollars; the form only
// accepts integer input.

import type {
  CreateSowInput,
  EvidenceLink,
  Sow,
  SowStatus,
  UpdateSowInput,
} from "../../../../types";

export interface SowFormState {
  name: string;
  /** Empty string = let backend auto-generate. Set when the user has
   *  clicked Edit and typed a custom value. Re-cleared when the user
   *  reverts to the auto-generated default. */
  number: string;
  numberOverrideMode: boolean;
  status: SowStatus;
  startDate: string;
  endDate: string;
  /** String for input fidelity. Empty = unset. Validated to a positive
   *  integer at submit time. */
  originalValue: string;
  description: string;
  evidenceLinks: EvidenceLink[];
  notes: string;
}

export type SowFieldErrors = Partial<
  Record<keyof SowFormState | "form", string>
>;

export function buildEmptySowFormState(): SowFormState {
  return {
    name: "",
    number: "",
    numberOverrideMode: false,
    status: "draft",
    startDate: "",
    endDate: "",
    originalValue: "",
    description: "",
    evidenceLinks: [],
    notes: "",
  };
}

export function stateFromSow(item: Sow): SowFormState {
  return {
    name: item.name,
    number: item.number,
    // Edit form treats numberOverrideMode as locked — the parent renders
    // AutoIdentifierField with editable=false so this flag is moot in
    // edit mode. Kept here for symmetry with the create flow.
    numberOverrideMode: false,
    status: item.status,
    startDate: item.startDate,
    endDate: item.endDate,
    originalValue: String(item.originalValue),
    description: item.description ?? "",
    evidenceLinks: item.evidenceLinks ? [...item.evidenceLinks] : [],
    notes: item.notes ?? "",
  };
}

/**
 * Build the backend Create payload from form state. Trims free-text
 * fields, parses originalValue to an integer, and only emits `number`
 * when the user has explicitly typed an override (empty string ⇒ omit
 * so the backend auto-generates).
 */
export function toCreateSowInput(
  state: SowFormState,
  contractId: string,
): CreateSowInput {
  const base: CreateSowInput = {
    contractId,
    name: state.name.trim(),
    startDate: state.startDate,
    endDate: state.endDate,
    originalValue: parseOriginalValue(state.originalValue),
  };
  if (state.numberOverrideMode && state.number.trim().length > 0) {
    base.number = state.number.trim();
  }
  if (state.status && state.status !== "draft") base.status = state.status;
  if (state.description.trim()) base.description = state.description.trim();
  if (state.evidenceLinks.length > 0) base.evidenceLinks = state.evidenceLinks;
  if (state.notes.trim()) base.notes = state.notes.trim();
  return base;
}

/**
 * Build the backend Update payload. Excludes `originalValue` and
 * `number` — both are immutable on the standard PUT path. The version
 * is supplied by the caller (the modal) since it lives on the source
 * record, not the form state.
 */
export function toUpdateSowInput(
  state: SowFormState,
  contractId: string,
  version: number,
): UpdateSowInput {
  const input: UpdateSowInput = {
    contractId,
    name: state.name.trim(),
    startDate: state.startDate,
    endDate: state.endDate,
    status: state.status,
    description: state.description.trim() || undefined,
    evidenceLinks:
      state.evidenceLinks.length > 0 ? state.evidenceLinks : undefined,
    notes: state.notes.trim() || undefined,
    version,
  };
  return input;
}

/** Parse the string-typed originalValue into a positive AUD amount.
 *  Up to 2 decimal places is preserved (cents precision). NaN / zero /
 *  negative values surface as 0 here and are rejected by form
 *  validation before submit. */
export function parseOriginalValue(raw: string): number {
  const cleaned = raw.replace(/[\s,$]/g, "");
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return 0;
  // Round to 2dp so the wire payload matches the backend monetaryAmount
  // invariant; floating-point inputs like 19.999 collapse to 20.00.
  return Math.max(0, Math.round(n * 100) / 100);
}
